import ShowRow from './ShowRow';
import { fancyTimeFormat, formatPublishedDate } from './timeHelper';
import { useEffect, useState } from 'react';

import EpisodeDescription from './EpisodeDescription';

import {
    AutoAwesome,
    CloseRounded,
    FormatListBulleted,
    RecordVoiceOver,
    Search,
    TextSnippetOutlined,
    YouTube,
} from '@mui/icons-material';
import { useEpisodePlayback, videoContinuation, resumeProgress } from '../utils/episodePlayback';
import { SwipeableDrawer } from '@mui/material';
import TranscriptPreview from './TranscriptPreview';
import SpeakerRows from './SpeakerRows';
import FullWidthButton from './FullWidthButton';
import HighlightsDrawer from './HighlightsDrawer';
import { isUnidentifiedSpeaker } from './speakerHelper';
import { getYouTubeVideoId } from './youtubeHelper';
import { getYouTubeProgressEntry, YOUTUBE_PROGRESS_EVENT } from './youtubeProgress';
import { useVideoNavigation } from '../utils/videoNavigation';

const NoteButtonIcon = ({ style }) => (
    <img
        src='/icons/note.png'
        alt=''
        aria-hidden='true'
        style={{ width: '16px', height: '16px', display: 'block', ...style }}
    />
);

export const TrackTranscriptUnavailableRow = () => (
    <div className='trackTranscriptUnavailableRow'>
        <span className='trackTranscriptUnavailableIcon' aria-hidden='true'>
            <TextSnippetOutlined style={{ fontSize: '15px' }} />
        </span>
        <span className='trackTranscriptUnavailableTitle'>
            Transcript unavailable
        </span>
    </div>
);

const TrackDetails = (props) => {
    const {
        track,
        userId,
        curTime,
        durationSeconds,
        playbackRate,
        showsPlaybackProgress,
        onTimestampPlay,
        previews,
        onAddNoteClick,
        onPodcastClick,
        onSearchClick,
        onSectionsClick,
        onTranscriptClick,
    } = props;

    const [showHighlights, setShowHighlights] = useState(false);
    const [showSpeakers, setShowSpeakers] = useState(false);
    const videoId = getYouTubeVideoId(track.youtube);
    const { openVideo } = useVideoNavigation();
    const progressIdentity = JSON.stringify([userId || null, videoId]);
    const [videoProgress, setVideoProgress] = useState(() => ({
        identity: progressIdentity, entry: getYouTubeProgressEntry(userId, videoId),
    }));
    useEffect(() => {
        const refresh = () => setVideoProgress({ identity: progressIdentity, entry: getYouTubeProgressEntry(userId, videoId) });
        refresh();
        window.addEventListener(YOUTUBE_PROGRESS_EVENT, refresh);
        window.addEventListener('storage', refresh);
        return () => {
            window.removeEventListener(YOUTUBE_PROGRESS_EVENT, refresh);
            window.removeEventListener('storage', refresh);
        };
    }, [progressIdentity, userId, videoId]);
    const history = useEpisodePlayback(userId, track);
    const savedVideo = history.videoProgress?.videoId === videoId ? history.videoProgress : null;
    const localVideo = videoProgress.identity === progressIdentity ? videoProgress.entry : null;
    const resumeSeconds = history.deletedAt ? 0 : savedVideo ? resumeProgress(savedVideo) : localVideo?.seconds || 0;
    const videoDuration = savedVideo ? savedVideo.durationSeconds : localVideo?.duration;
    const videoSubtitle = resumeSeconds < 1 ? null : Number.isFinite(videoDuration) && videoDuration > 0
        ? `-${fancyTimeFormat(Math.max(0, videoDuration - resumeSeconds))}` : 'Continue watching';
    const watchVideo = () => {
        openVideo(track.id, videoId, track, {
            audioContinuationSeconds: videoContinuation(history, videoId),
            continuationUserId: userId || null,
        });
    };

    const numNotes = (track.transcript || []).reduce((count, message) => count + (message.numNotes ?? 0), 0);
    const noteButtonTitle = numNotes ? (numNotes === 1 ? `${numNotes} note` : `${numNotes} notes`) : 'Add a note';
    const hasDescription = !!track.description?.trim();
    const hasHighlights = !!previews?.length;
    const knownSpeakers = (track.speakers || []).filter((speaker) => !isUnidentifiedSpeaker(speaker));
    const hasSpeakers = knownSpeakers.length > 0;
    const publishedDate = formatPublishedDate(track.publishedDate);
    const titleClassName = track.source.title && track.source.title.length > 150 ? 'heading' : 'largeHeading';
    const actionRowButtonClassName = 'trackDetailsActionRow';

    const noteAction = {
        key: 'notes',
        Icon: NoteButtonIcon,
        title: noteButtonTitle,
        onClick: onAddNoteClick,
        iconStyle: { width: '20px', height: '20px' },
    };

    const detailActions = track.aiFeatures ? [
        {
            key: 'sections',
            Icon: FormatListBulleted,
            title: 'Sections',
            onClick: onSectionsClick,
        },
        {
            key: 'search',
            Icon: Search,
            title: 'Search',
            onClick: onSearchClick,
        },
        hasHighlights ? {
            key: 'highlights',
            Icon: AutoAwesome,
            title: 'Highlights',
            onClick: () => setShowHighlights(true),
        } : null,
        hasSpeakers ? {
            key: 'speakers',
            Icon: RecordVoiceOver,
            title: 'Speakers',
            onClick: () => setShowSpeakers(true),
        } : null,
        noteAction,
    ].filter(Boolean) : [noteAction];
    if (videoId) {
        detailActions.push({
            key: 'video',
            Icon: YouTube,
            title: 'Watch video',
            subtitle: videoSubtitle,
            onClick: watchVideo,
        });
    }

    const actionRows = (
        <div className='trackDetailsActionGroup'>
            {track.aiFeatures && (
                <TranscriptPreview
                    curTime={curTime}
                    track={track}
                    onTranscriptClick={onTranscriptClick}
                />
            )}
            {detailActions.map((action, index) => (
                <div key={action.key}>
                    <FullWidthButton
                        Icon={action.Icon}
                        title={action.title}
                        subtitle={action.subtitle}
                        onClick={action.onClick}
                        contentGap='14px'
                        iconStyle={action.iconStyle || { fontSize: '20px', verticalAlign: 'middle' }}
                        buttonClassName={actionRowButtonClassName}
                    />
                    {index !== detailActions.length - 1 && (
                        <div className='trackDetailsActionDivider' aria-hidden='true' />
                    )}
                </div>
            ))}
        </div>
    );

    return (
        <div>
            <div className='trackDetailsTopSection'>
                <div className='trackDetailsHeaderCard'>
                    <ShowRow
                        source={track.source}
                        onOpen={onPodcastClick}
                        openLabel={`Open ${track.source.name}`}
                        description=''
                    />

                    <div>
                        <div className={`trackDetailsEpisodeTitle ${titleClassName}`}>
                            {track.source.title}
                        </div>
                        {publishedDate && (
                            <time className='trackDetailsPublishedDate' dateTime={track.publishedDate}>
                                {publishedDate}
                            </time>
                        )}
                    </div>
                </div>
            </div>

            <div className='trackDetailsPlaybackSection'>
                {actionRows}
            </div>

            {hasDescription && (
                <div className='trackDetailsDescriptionSection'>
                    <EpisodeDescription
                        curTime={curTime}
                        description={track.description ?? ''}
                        durationSeconds={durationSeconds ?? track.totalTimeSeconds}
                        playbackRate={playbackRate}
                        showsPlaybackProgress={showsPlaybackProgress}
                        onTimestampPlay={onTimestampPlay}
                    />
                </div>
            )}

            <HighlightsDrawer
                open={showHighlights}
                onClose={() => setShowHighlights(false)}
                previews={previews || []}
                previewTitle={track.source.title || 'Highlights'}
                playbackOrigin={{ kind: 'episode' }}
            />

            <SwipeableDrawer
                anchor='bottom'
                open={showSpeakers}
                onClose={() => setShowSpeakers(false)}
                onOpen={() => {}}
                disableDiscovery
                ModalProps={{ keepMounted: false }}
                PaperProps={{
                    sx: {
                        borderRadius: '18px 18px 0px 0px',
                        maxHeight: 'calc(var(--app-drawer-height, 100vh) * 0.94)',
                    },
                }}
            >
                <div className='trackDetailsSpeakersDrawer horizontalCenter appDrawerSurface'>
                    <div className='previewPreferenceDragIndicator' />
                    <div className='trackDetailsDrawerHeader'>
                        <h2 className='trackDetailsDrawerTitle'>Speakers</h2>
                        <button
                            type='button'
                            className='trackDetailsDrawerCloseButton'
                            onClick={() => setShowSpeakers(false)}
                            aria-label='Close speakers'
                        >
                            <CloseRounded style={{ fontSize: '20px' }} />
                        </button>
                    </div>
                    <div className='highlightsDrawerDivider' />
                    <div className='trackDetailsSpeakersDrawerRows hideScrollbar'>
                        <SpeakerRows speakers={knownSpeakers} profileBackLabel='Back to speakers' />
                    </div>
                </div>
            </SwipeableDrawer>
        </div >
    );
}

TrackDetails.propTypes = {

}

TrackDetails.defaultProps = {

}

export default TrackDetails;
